import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PencilSimple, Receipt, BookOpen } from '@phosphor-icons/react';
import PaymentReceiptDialog from '@/components/PaymentReceiptDialog';
import { useNavigate } from 'react-router-dom';

const fmt = (n) => `\u20B9${(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Invoice-scoped ledger pop-up. Fetches the customer's full ledger, then filters
 * down to rows belonging to THIS invoice — so the user sees the bill line plus
 * every receipt in chronological order, with an edit pencil per row.
 */
export default function InvoiceLedgerDialog({ invoice, onOpenChange, accounts, canEdit, onChanged }) {
  const open = !!invoice;
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [freshInvoice, setFreshInvoice] = useState(null);

  useEffect(() => {
    if (!open || !invoice) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [lres, ires] = await Promise.all([
          api.get(`/customers/${invoice.customer_id}/ledger`),
          api.get(`/invoices/${invoice.id}`),
        ]);
        if (!cancelled) {
          setRows((lres.data || []).filter(r => r.invoice_id === invoice.id));
          setFreshInvoice(ires.data);
        }
      } catch {
        if (!cancelled) toast.error('Unable to load ledger for this invoice');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, invoice]);

  const totals = useMemo(() => {
    const d = rows.reduce((s, r) => s + (r.debit || 0), 0);
    const c = rows.reduce((s, r) => s + (r.credit || 0), 0);
    return { debit: d, credit: c, balance: d - c };
  }, [rows]);

  if (!invoice) return null;

  const refresh = async () => {
    const [lres, ires] = await Promise.all([
      api.get(`/customers/${invoice.customer_id}/ledger`),
      api.get(`/invoices/${invoice.id}`),
    ]);
    setRows((lres.data || []).filter(r => r.invoice_id === invoice.id));
    setFreshInvoice(ires.data);
    onChanged?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-w-4xl" data-testid="invoice-ledger-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen size={20} weight="duotone" className="text-[#3B82F6]" />
            Invoice Ledger <span className="font-mono text-[#3B82F6]">{invoice.invoice_number}</span>
          </DialogTitle>
          <DialogDescription className="flex items-center gap-3 flex-wrap">
            <span className="text-[#0A0A0A] font-semibold">{invoice.customer_name}</span>
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#52525B]">Bill {fmt(invoice.total)}</span>
            <span className={`text-[10px] uppercase tracking-[0.15em] ${invoice.balance_due > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>Due {fmt(invoice.balance_due)}</span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="p-8 text-center text-sm text-[#52525B]">Loading...</p>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center text-sm text-[#52525B]">No ledger entries for this invoice.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-[#E4E4E7] bg-[#FAFAFA]">
                <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Date</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Description</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Receipt</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Debit</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Credit</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-center w-14">Edit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i} className={r.type === 'adjustment' ? 'bg-[#F8FAFC] italic' : 'border-[#E4E4E7]'}>
                  <TableCell className="text-xs text-[#52525B] whitespace-nowrap">{new Date(r.date).toLocaleDateString('en-IN')}</TableCell>
                  <TableCell className="text-sm">
                    {r.type === 'invoice' ? (
                      <span><Badge className="rounded-sm text-[9px] bg-[#DBEAFE] text-[#2563EB] mr-1.5" variant="outline">BILL</Badge>{r.description}</span>
                    ) : r.type === 'adjustment' ? (
                      <span className="text-[#64748B]"><Badge className="rounded-sm text-[9px] bg-[#64748B]/10 text-[#475569] mr-1.5" variant="outline">ADJ</Badge>{r.description}</span>
                    ) : (
                      <span><Badge className="rounded-sm text-[9px] bg-[#D1FAE5] text-[#059669] mr-1.5" variant="outline">PAYMENT</Badge>{r.description}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs font-mono text-[#059669]">{r.receipt_number || <span className="text-[#A1A1AA]">—</span>}</TableCell>
                  <TableCell className="text-sm text-right font-mono">{(r.debit || 0) > 0 ? fmt(r.debit) : <span className="text-[#A1A1AA]">—</span>}</TableCell>
                  <TableCell className="text-sm text-right font-mono text-[#10B981]">{(r.credit || 0) > 0 ? fmt(r.credit) : <span className="text-[#A1A1AA]">—</span>}</TableCell>
                  <TableCell className="text-center">
                    {canEdit && r.type === 'invoice' && (
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[#52525B] hover:text-[#3B82F6]"
                        onClick={() => { onOpenChange?.(false); navigate(`/invoices/edit/${r.invoice_id}`); }}
                        title="Edit invoice"
                        data-testid={`il-edit-inv-${r.invoice_id}`}>
                        <PencilSimple size={13} />
                      </Button>
                    )}
                    {canEdit && r.type === 'payment' && r.receipt_number && (
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[#52525B] hover:text-[#10B981]"
                        onClick={() => setEditingReceipt({
                          receipt_number: r.receipt_number,
                          amount: r.credit,
                          payment_mode: r.payment_mode,
                          date: r.date,
                        })}
                        title={`Edit ${r.receipt_number}`}
                        data-testid={`il-edit-rcpt-${r.receipt_number}`}>
                        <PencilSimple size={13} />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!loading && rows.length > 0 && (
          <div className="flex items-center justify-end gap-4 pt-2 border-t border-[#E4E4E7] text-sm font-mono">
            <span className="text-[#52525B]">Debit <span className="font-bold">{fmt(totals.debit)}</span></span>
            <span className="text-[#52525B]">Credit <span className="font-bold text-[#10B981]">{fmt(totals.credit)}</span></span>
            <span className={`font-bold ${totals.balance > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>Balance {fmt(totals.balance)}</span>
          </div>
        )}

        <PaymentReceiptDialog
          open={!!editingReceipt}
          onOpenChange={(o) => { if (!o) setEditingReceipt(null); }}
          invoice={freshInvoice || invoice}
          receipt={editingReceipt}
          accounts={accounts}
          onSaved={refresh}
        />
      </DialogContent>
    </Dialog>
  );
}

// Make the icons importable from a barrel so InvoicesPage doesn't need them directly
export { Receipt };
